import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Sentry } from '@/lib/sentry';
import { AUTH_ENABLED } from '@/lib/prelaunch';
import AppShell from '@/components/layout/AppShell';
import AnimatedLayout from '@/components/layout/AnimatedLayout';
import ProtectedRoute from './ProtectedRoute';
import OnboardingRoute from './OnboardingRoute';
import InterviewLayout from './InterviewLayout';
import ErrorBoundaryPage from '@/components/error/ErrorBoundaryPage';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

import IndexPage from '@/pages/IndexPage';
import AuthPage from '@/pages/AuthPage';
import VerifyOtpPage from '@/pages/VerifyOtpPage';
import SettingsPage from '@/pages/SettingsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';

import NamePage from '@/pages/onboarding/NamePage';
import InterviewDatePage from '@/pages/onboarding/InterviewDatePage';
import EnglishLevelPage from '@/pages/onboarding/EnglishLevelPage';
import ConcernsPage from '@/pages/onboarding/ConcernsPage';
import ValuePreviewPage from '@/pages/onboarding/ValuePreviewPage';
import QuickWinPage from '@/pages/onboarding/QuickWinPage';
import SoftPaywallPage from '@/pages/onboarding/SoftPaywallPage';
import OnboardingWelcomePage from '@/pages/onboarding/OnboardingWelcomePage';

import HomePage from '@/pages/dashboard/HomePage';
import PracticePage from '@/pages/dashboard/PracticePage';
import PricingPage from '@/pages/PricingPage';
import SubscriptionSuccessPage from '@/pages/subscription/SuccessPage';

import IntroPage from '@/pages/interview/IntroPage';
import QuestionPage from '@/pages/interview/QuestionPage';
import ResultsPage from '@/pages/interview/ResultsPage';

export const router = sentryCreateBrowserRouter([
  {
    element: <AnimatedLayout />,
    errorElement: <ErrorBoundaryPage />,
    children: [
      { path: '/', element: <IndexPage /> },
      { path: '/welcome', element: <Navigate to="/" replace /> },
      {
        path: '/auth',
        element: AUTH_ENABLED ? <AuthPage /> : <Navigate to="/" replace />,
      },
      {
        path: '/verify-otp',
        element: AUTH_ENABLED ? <VerifyOtpPage /> : <Navigate to="/" replace />,
      },
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/terms', element: <TermsPage /> },
      {
        element: <OnboardingRoute />,
        children: [
          { path: '/onboarding/name', element: <NamePage /> },
          { path: '/onboarding/interview-date', element: <InterviewDatePage /> },
          { path: '/onboarding/english-level', element: <EnglishLevelPage /> },
          { path: '/onboarding/concerns', element: <ConcernsPage /> },
          { path: '/onboarding/value-preview', element: <ValuePreviewPage /> },
          { path: '/onboarding/quick-win', element: <QuickWinPage /> },
          { path: '/onboarding/soft-paywall', element: <SoftPaywallPage /> },
          { path: '/onboarding/welcome', element: <OnboardingWelcomePage /> },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppShell />,
            children: [
              { path: '/home', element: <HomePage /> },
              { path: '/practice', element: <PracticePage /> },
              { path: '/settings', element: <SettingsPage /> },
            ],
          },
          {
            element: <InterviewLayout />,
            children: [
              { path: '/interview/intro', element: <IntroPage /> },
              { path: '/interview/question', element: <QuestionPage /> },
              { path: '/interview/results', element: <ResultsPage /> },
            ],
          },
          { path: '/pricing', element: <PricingPage /> },
          { path: '/subscription/success', element: <SubscriptionSuccessPage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
